/**
 * @file View model for the portfolio's Past collaborations block (EPD-10120).
 *
 * Built from the creator's Brand Collaboration Highlights (`brandHighlights` on
 * the public profile payload) plus the read-through post stats that stream in
 * separately (EPD-11945). Pure functions so the grid and case-study panel stay
 * presentational and the rules here are unit-testable.
 */

import { fmtCount } from "@/lib/portfolio/metric-catalog";
import { resolvePostThumbnails } from "@/lib/portfolio/post-thumbnail";
import type {
  HighlightPostStat,
  PublicBrandHighlight,
  PublicHighlightPostStats,
} from "@/lib/queries/public-profile";

type HighlightItem = PublicBrandHighlight["items"][number];

/** One asset in a case study's media strip. */
export type CollaborationMedia =
  | { kind: "image"; key: string; url: string; alt: string }
  | { kind: "video"; key: string; url: string; label: string }
  /**
   * A social post link. `thumbnails` are the candidates to try in order
   * (EPD-12059, see `resolvePostThumbnails`); empty shows the placeholder.
   */
  | {
      kind: "post";
      key: string;
      url: string;
      host: string;
      thumbnails: string[];
    };

/** Extra thumbnail sources for post links (EPD-12059). */
export interface ThumbnailContext {
  /** Recent-post images keyed by platform + post id (`indexRecentPostImages`). */
  recentPostImages?: ReadonlyMap<string, string>;
  /** Clock for the signed-CDN expiry check; defaults to now. */
  nowMs?: number;
}

export interface CollaborationStat {
  label: "Views" | "Likes" | "Comments" | "Engagement rate";
  value: string;
}

export interface Collaboration {
  key: string;
  name: string;
  /**
   * The brand's logo (EPD-12217), or null to show the name. Only for a
   * highlight with a brand name: a cell labelled by the post's host has none.
   */
  logoUrl: string | null;
  /** `{category} · {year}`, omitting whichever part is missing. */
  eyebrow: string | null;
  summary: string | null;
  media: CollaborationMedia[];
  stats: CollaborationStat[];
  /** Media, a summary or stats: worth opening a case study for. */
  hasCaseStudy: boolean;
}

export function hostnameOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
}

export function isHttpUrl(url: string): boolean {
  try {
    const { protocol } = new URL(url);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Same rule as the portfolio highlight tiles: only a supported post with a
 * thumbnail or a number is worth showing; unsupported and still-pending links
 * aren't.
 */
export function isRenderableStat(
  stat: HighlightPostStat | undefined,
): stat is HighlightPostStat {
  if (!stat?.supported) return false;
  return (
    stat.thumbnail != null ||
    stat.views != null ||
    stat.likes != null ||
    stat.comments != null ||
    stat.saves != null ||
    stat.shares != null ||
    stat.engagementRate != null
  );
}

/** "2024", or "2023–2024" for a collaboration that spans years. */
export function collaborationYear(
  start: Date | null,
  end: Date | null,
): string | null {
  if (!start) return end ? String(end.getFullYear()) : null;
  const from = start.getFullYear();
  const to = end?.getFullYear();
  return to != null && to !== from ? `${from}–${to}` : String(from);
}

function toMedia(
  item: HighlightItem,
  index: number,
  postStats: PublicHighlightPostStats | undefined,
  brandName: string,
  thumbnailContext: ThumbnailContext,
): CollaborationMedia | null {
  const key = item.id ?? `${index}-${item.url}`;
  if (item.kind === "upload") {
    if (item.mimeType?.startsWith("image/")) {
      return {
        kind: "image",
        key,
        url: item.url,
        alt: item.fileName ?? brandName,
      };
    }
    if (item.mimeType?.startsWith("video/")) {
      return {
        kind: "video",
        key,
        url: item.url,
        label: item.fileName ?? `${brandName} video`,
      };
    }
    return null;
  }
  if (!isHttpUrl(item.url)) return null;
  const stat = postStats?.[item.url];
  return {
    kind: "post",
    key,
    url: item.url,
    host: hostnameOf(item.url),
    thumbnails: resolvePostThumbnails({
      url: item.url,
      statThumbnail: isRenderableStat(stat) ? stat.thumbnail : null,
      recentPostImages: thumbnailContext.recentPostImages,
      // Expiry is checked once, when the collaborations are built. A link that
      // expires while the page stays open is still tried first; the UI's
      // onError fallback then advances to the next candidate.
      nowMs: thumbnailContext.nowMs ?? Date.now(),
    }),
  };
}

/** Engagement rate arrives as a decimal fraction (0.024 = 2.4%). */
function formatRate(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

/**
 * Aggregate the stats of every linked post with live numbers. Counts are
 * summed. Engagement rate can't be recomputed from the sums because its
 * denominator (reach / followers, decided upstream) isn't exposed, and
 * engagements ÷ views would be a different metric from the per-post rate shown
 * elsewhere. So it is the views-weighted mean of the per-post rates, which is
 * the pooled rate whenever the denominator tracks views. Posts without views
 * fall back to an unweighted mean.
 */
export function aggregatePostStats(
  stats: readonly HighlightPostStat[],
): CollaborationStat[] {
  const live = stats.filter(isRenderableStat);
  const sum = (pick: (s: HighlightPostStat) => number | null) => {
    const values = live.map(pick).filter((v): v is number => v != null);
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) : null;
  };

  const views = sum((s) => s.views);
  const likes = sum((s) => s.likes);
  const comments = sum((s) => s.comments);

  const rated = live.filter(
    (s): s is HighlightPostStat & { engagementRate: number } =>
      s.engagementRate != null,
  );
  const weighted = rated.filter((s) => (s.views ?? 0) > 0);
  let rate: number | null = null;
  if (weighted.length > 0) {
    const totalViews = weighted.reduce((a, s) => a + (s.views ?? 0), 0);
    rate =
      weighted.reduce((a, s) => a + s.engagementRate * (s.views ?? 0), 0) /
      totalViews;
  } else if (rated.length > 0) {
    rate = rated.reduce((a, s) => a + s.engagementRate, 0) / rated.length;
  }

  const out: CollaborationStat[] = [];
  if (views != null) out.push({ label: "Views", value: fmtCount(views) });
  if (likes != null) out.push({ label: "Likes", value: fmtCount(likes) });
  if (comments != null)
    out.push({ label: "Comments", value: fmtCount(comments) });
  if (rate != null)
    out.push({ label: "Engagement rate", value: formatRate(rate) });
  return out;
}

/**
 * The label of a highlight's cell: its brand name, else its post link's host.
 * Empty when it has neither.
 */
function collaborationName(highlight: PublicBrandHighlight): string {
  const brandName = highlight.brandName?.trim();
  return (
    brandName ||
    (highlight.primaryPostUrl ? hostnameOf(highlight.primaryPostUrl) : "")
  );
}

/** Whether any highlight makes a grid entry (see toCollaborations). */
export function hasCollaborations(
  highlights: readonly PublicBrandHighlight[],
): boolean {
  return highlights.some((highlight) => collaborationName(highlight) !== "");
}

/**
 * Map stored highlights to grid entries, in the creator's order. A highlight
 * with neither a brand name nor a post link has nothing to label its cell
 * with, so it is skipped.
 */
export function toCollaborations(
  highlights: readonly PublicBrandHighlight[],
  postStats?: PublicHighlightPostStats,
  thumbnailContext: ThumbnailContext = {},
): Collaboration[] {
  return highlights.flatMap((highlight, index) => {
    const brandName = highlight.brandName?.trim();
    const name = collaborationName(highlight);
    if (!name) return [];

    const media = highlight.items.flatMap((item, i) => {
      const m = toMedia(item, i, postStats, name, thumbnailContext);
      return m ? [m] : [];
    });
    // Only link items have live stats; uploads never contribute.
    const stats = aggregatePostStats(
      highlight.items.flatMap((item) => {
        const stat = item.kind === "link" ? postStats?.[item.url] : undefined;
        return stat ? [stat] : [];
      }),
    );
    const eyebrow =
      [
        highlight.category?.trim(),
        collaborationYear(highlight.startDate, highlight.endDate),
      ]
        .filter(Boolean)
        .join(" · ") || null;
    const summary = highlight.description?.trim() || null;

    return [
      {
        key: highlight.id ?? `legacy-${index}`,
        name,
        logoUrl: brandName ? (highlight.brandLogoUrl ?? null) : null,
        eyebrow,
        summary,
        media,
        stats,
        hasCaseStudy: media.length > 0 || summary != null || stats.length > 0,
      },
    ];
  });
}

/**
 * Empty cells that pad the last row of a `columns`-wide grid so it stays
 * rectangular.
 */
export function fillerCount(items: number, columns: number): number {
  return (columns - (items % columns)) % columns;
}

/**
 * Tiles per row for the published Past collaborations grid (EPD-12479): rows
 * of up to `perRow` tiles that share the row's width, so no row has empty
 * boxes. A single tile left over takes one from the row above when that row
 * can spare it (with 3 per row, 4 → 2 + 2 and 7 → 3 + 2 + 2); with 2 per row
 * it can't, so an odd count ends with one tile across the full row.
 */
export function collaborationRows(items: number, perRow: number): number[] {
  if (items <= 0) return [];
  const rows = Array<number>(Math.floor(items / perRow)).fill(perRow);
  const rest = items % perRow;
  if (rest === 0) return rows;
  if (rest === 1 && rows.length > 0 && perRow > 2) {
    rows[rows.length - 1] = perRow - 1;
    rows.push(2);
    return rows;
  }
  rows.push(rest);
  return rows;
}
